import { useParams } from 'react-router-dom';
import { PublicShell } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { FormRenderer, formSchemaSchema, usePublicSubmission } from '../../features/forms/index.js';
import type { FormSchema } from '../../features/forms/index.js';

import { OnboardingErrorState } from './OnboardingErrorState.js';
import { OnboardingLoadingState } from './OnboardingLoadingState.js';
import { OnboardingSubmittedState } from './OnboardingSubmittedState.js';
import { OnboardingFormRuntime } from './OnboardingFormRuntime.js';

/**
 * Rota pública `/onboarding/:token` — renderiza o questionário para o
 * lead responder. Sem login, sem guard — token da URL é o segredo.
 *
 * Estados possíveis:
 *   - `loading`    → skeleton
 *   - `error`      → "não encontramos este link" (404/rede/etc)
 *   - `expired`    → "link expirado, peça novo"
 *   - `submitted`  → "obrigado, já respondemos em X"
 *   - `pending` / `in_progress` → `FormRenderer` com auto-save
 *
 * Mobile-first: `PublicShell` já entrega safe-area + max-width
 * responsivo. Consome só primitivos de `@kmgestao/ui` e tokens
 * semânticos — troca de paleta reflete aqui automaticamente.
 */
export function OnboardingFormPage() {
  const { token } = useParams<{ token: string }>();
  const query = usePublicSubmission(token);

  return (
    <PublicShell
      brand={<Brand onDark={false} />}
      footer={
        <p className="text-xs">KM Gestão · consultoria e gestão para clínicas brasileiras.</p>
      }
    >
      <OnboardingFormContent
        token={token ?? ''}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage={query.error?.message}
        data={query.data}
      />
    </PublicShell>
  );
}

interface OnboardingFormContentProps {
  token: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string | undefined;
  data?: ReturnType<typeof usePublicSubmission>['data'];
}

/**
 * Roteador de estado da rota. Separado do `OnboardingFormPage` para
 * manter o componente principal enxuto (≤ 200 linhas). Decide qual
 * das 4 telas de estado renderizar.
 */
function OnboardingFormContent({
  token,
  isLoading,
  isError,
  errorMessage,
  data,
}: OnboardingFormContentProps) {
  if (!token) {
    return <OnboardingErrorState variant="not_found" />;
  }
  if (isLoading) return <OnboardingLoadingState />;
  if (isError || !data) {
    return <OnboardingErrorState variant="not_found" details={errorMessage} />;
  }

  const { submission, template } = data;

  if (submission.status === 'expired') {
    return <OnboardingErrorState variant="expired" />;
  }

  if (submission.status === 'submitted') {
    return (
      <OnboardingSubmittedState
        submittedAt={submission.submitted_at}
        templateName={template.name_pt_br}
      />
    );
  }

  // pending | in_progress — valida schema e renderiza o form.
  // Se o schema do banco está corrompido (nunca deveria, mas defesa),
  // cai no estado de erro em vez de crashear.
  const parsed = formSchemaSchema.safeParse(template.schema);
  if (!parsed.success) {
    return (
      <OnboardingErrorState
        variant="not_found"
        details="Template corrompido. Avise a consultora."
      />
    );
  }

  return (
    <OnboardingFormRuntime
      token={token}
      templateName={template.name_pt_br}
      schema={parsed.data as FormSchema}
      initialAnswers={submission.partial_answers}
    />
  );
}

// Reexporta um Renderer-wrapper para Playwright poder importar direto.
export { FormRenderer as PublicFormRenderer };
